import React, { useEffect, useState } from 'react';
import { useServiceOS } from '../hooks/useServiceOS';
import SOSDeploySection from '../components/SOSDeploySection';
import {
  CheckCircle,
  Copy,
  EnvelopeSimple,
  PaperPlaneTilt,
  ArrowsClockwise,
} from '@phosphor-icons/react';

export default function SOSSettings() {
  const { getProfile, completeOnboarding, sendTestEmail } = useServiceOS();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testStatus, setTestStatus] = useState(''); // '' | 'sending' | 'done' | 'error'
  const [form, setForm] = useState(null);

  useEffect(() => {
    getProfile().then(res => {
      setProfile(res.profile);
      if (res.profile) setForm({ ...res.profile });
    }).catch(console.error).finally(() => setLoading(false));
  }, [getProfile]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await completeOnboarding({
        business_name: form.business_name,
        business_type: form.business_type,
        working_days: form.working_days,
        workday_start_time: form.workday_start_time,
        workday_end_time: form.workday_end_time,
        default_job_duration_minutes: form.default_job_duration_minutes,
        buffer_minutes: form.buffer_minutes,
        timezone: form.timezone,
        services_offered: form.services_offered,
        services_not_offered: form.services_not_offered,
        service_area: form.service_area,
        business_description: form.business_description,
        pricing_notes: form.pricing_notes,
        callout_fee_text: form.callout_fee_text,
        hourly_rate: form.hourly_rate,
      });
      setProfile(res.profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!profile?.lead_inbox_email) return;
    try {
      await navigator.clipboard.writeText(profile.lead_inbox_email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = profile.lead_inbox_email;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTestEmail = async () => {
    setTestStatus('sending');
    try {
      await sendTestEmail();
      setTestStatus('done');
      setTimeout(() => setTestStatus(''), 4000);
    } catch (e) {
      console.error(e);
      setTestStatus('error');
      setTimeout(() => setTestStatus(''), 4000);
    }
  };

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day],
    }));
  };

  if (loading || !form) {
    return (
      <div className="flex justify-center py-16" data-testid="settings-loading">
        <div className="w-5 h-5 border-2 border-[#14F195] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="sos-settings">
      <div>
        <h1 className="text-xl font-bold text-white">Settings</h1>
        <p className="text-sm text-[#8B8FA3]">Manage your business profile and lead inbox</p>
      </div>

      {/* ── Lead Inbox Email — Primary CTA ── */}
      <div className="bg-[#12131A] border border-[#14F195]/20 rounded-xl p-6" data-testid="inbox-email-section">
        <div className="flex items-center gap-2 mb-3">
          <EnvelopeSimple size={18} className="text-[#14F195]" weight="duotone" />
          <h2 className="text-sm font-semibold text-white">Lead Inbox</h2>
        </div>
        <p className="text-xs text-[#8B8FA3] mb-4">
          Forward customer enquiries to this email. We'll extract the details, draft a reply, and notify you automatically.
        </p>

        {profile?.lead_inbox_email ? (
          <>
            <div className="flex items-stretch gap-2 mb-4">
              <div className="flex-1 flex items-center px-4 py-3 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg">
                <code className="text-sm text-[#14F195] font-mono break-all select-all" data-testid="inbox-email-address">
                  {profile.lead_inbox_email}
                </code>
              </div>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-4 rounded-lg text-sm font-medium transition-colors shrink-0 ${
                  copied
                    ? 'bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/30'
                    : 'bg-[#14F195]/10 text-[#14F195] border border-[#14F195]/30 hover:bg-[#14F195]/20'
                }`}
                data-testid="copy-inbox-email-btn"
              >
                {copied ? <><CheckCircle size={14} weight="fill" /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleTestEmail}
                disabled={testStatus === 'sending'}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                  testStatus === 'done'
                    ? 'bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/30'
                    : testStatus === 'error'
                    ? 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30'
                    : 'bg-[#0B0C10] text-[#8B8FA3] border border-[#1E1F2E] hover:text-white hover:border-[#2E2F3E]'
                }`}
                data-testid="send-test-email-btn"
              >
                {testStatus === 'sending' ? (
                  <><ArrowsClockwise size={12} className="animate-spin" /> Sending test...</>
                ) : testStatus === 'done' ? (
                  <><CheckCircle size={12} weight="fill" /> Test lead created</>
                ) : testStatus === 'error' ? (
                  <>Test failed — try again</>
                ) : (
                  <><PaperPlaneTilt size={12} /> Send test email</>
                )}
              </button>
              {testStatus === 'done' && (
                <span className="text-[10px] text-[#8B8FA3]">Check your Leads page</span>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-[#555]">Inbox email will appear after onboarding is complete.</p>
        )}
      </div>

      {/* ── Website Enquiry Form ── */}
      <SOSDeploySection />

      {/* ── Business Context — Tell us about your business ── */}
      <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-6 space-y-5" data-testid="business-context-section">
        <div>
          <h2 className="text-sm font-semibold text-white">Tell us about your business</h2>
          <p className="text-xs text-[#8B8FA3] mt-1">This helps us write accurate replies and guide website enquiries.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">What services do you offer?</label>
          <textarea
            rows={2}
            value={form.services_offered || ''}
            onChange={e => setForm(f => ({ ...f, services_offered: e.target.value }))}
            placeholder="e.g. Boiler repairs, central heating installation, emergency plumbing, bathroom fitting"
            className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50 placeholder:text-[#555]"
            data-testid="settings-services-offered"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">What jobs do you not do? <span className="text-[#555] font-normal">(optional)</span></label>
          <textarea
            rows={2}
            value={form.services_not_offered || ''}
            onChange={e => setForm(f => ({ ...f, services_not_offered: e.target.value }))}
            placeholder="e.g. We don't do gas work, roofing, or commercial properties"
            className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50 placeholder:text-[#555]"
            data-testid="settings-services-not-offered"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">What areas do you service?</label>
          <input
            value={form.service_area || ''}
            onChange={e => setForm(f => ({ ...f, service_area: e.target.value }))}
            placeholder="e.g. Greater Manchester, within 15 miles of M1"
            className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50 placeholder:text-[#555]"
            data-testid="settings-service-area"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Anything customers should know? <span className="text-[#555] font-normal">(optional)</span></label>
          <textarea
            rows={2}
            value={form.business_description || ''}
            onChange={e => setForm(f => ({ ...f, business_description: e.target.value }))}
            placeholder="e.g. Family-run business, 20 years experience, all work guaranteed"
            className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50 placeholder:text-[#555]"
            data-testid="settings-business-description"
          />
        </div>
      </div>

      {/* ── Pricing & Callout ── */}
      <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-6 space-y-5" data-testid="pricing-section">
        <div>
          <h2 className="text-sm font-semibold text-white">Pricing & Callout</h2>
          <p className="text-xs text-[#8B8FA3] mt-1">Optional. Used to set customer expectations in replies — not for exact quotes.</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Callout fee</label>
            <input
              value={form.callout_fee_text || ''}
              onChange={e => setForm(f => ({ ...f, callout_fee_text: e.target.value }))}
              placeholder="e.g. From $150"
              className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50 placeholder:text-[#555]"
              data-testid="settings-callout-fee"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Hourly rate</label>
            <input
              value={form.hourly_rate || ''}
              onChange={e => setForm(f => ({ ...f, hourly_rate: e.target.value }))}
              placeholder="e.g. $80/hr"
              className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50 placeholder:text-[#555]"
              data-testid="settings-hourly-rate"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Additional pricing notes <span className="text-[#555] font-normal">(optional)</span></label>
          <input
            value={form.pricing_notes || ''}
            onChange={e => setForm(f => ({ ...f, pricing_notes: e.target.value }))}
            placeholder="e.g. Free quotes on larger jobs, emergency surcharge may apply"
            className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50 placeholder:text-[#555]"
            data-testid="settings-pricing-notes"
          />
        </div>
      </div>

      {/* ── Business Profile ── */}
      <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-6 space-y-5">
        <h2 className="text-sm font-semibold text-white">Business profile</h2>

        <div>
          <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Business name</label>
          <input
            value={form.business_name}
            onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
            className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
            data-testid="settings-business-name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Business type</label>
          <select
            value={form.business_type}
            onChange={e => setForm(f => ({ ...f, business_type: e.target.value }))}
            className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
            data-testid="settings-business-type"
          >
            {['plumbing', 'electrical', 'hvac', 'cleaning', 'landscaping', 'painting', 'roofing', 'other'].map(t => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Working days</label>
          <div className="flex gap-2 flex-wrap">
            {DAYS.map(d => (
              <button
                key={d}
                onClick={() => toggleDay(d)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  form.working_days.includes(d)
                    ? 'border-[#14F195] bg-[#14F195]/10 text-[#14F195]'
                    : 'border-[#1E1F2E] text-[#8B8FA3] hover:border-[#2E2F3E]'
                }`}
                data-testid={`settings-day-${d}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Start time</label>
            <input
              type="time"
              value={form.workday_start_time}
              onChange={e => setForm(f => ({ ...f, workday_start_time: e.target.value }))}
              className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
              data-testid="settings-start-time"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">End time</label>
            <input
              type="time"
              value={form.workday_end_time}
              onChange={e => setForm(f => ({ ...f, workday_end_time: e.target.value }))}
              className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
              data-testid="settings-end-time"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Default job duration</label>
            <select
              value={form.default_job_duration_minutes}
              onChange={e => setForm(f => ({ ...f, default_job_duration_minutes: parseInt(e.target.value) }))}
              className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
              data-testid="settings-duration"
            >
              <option value={30}>30 min</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Buffer between jobs</label>
            <select
              value={form.buffer_minutes}
              onChange={e => setForm(f => ({ ...f, buffer_minutes: parseInt(e.target.value) }))}
              className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
              data-testid="settings-buffer"
            >
              <option value={0}>None</option>
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span className="flex items-center gap-1 text-sm text-[#22C55E]" data-testid="settings-saved">
            <CheckCircle size={14} weight="fill" /> Saved
          </span>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-[#14F195] text-[#0B0C10] rounded-lg text-sm font-semibold hover:bg-[#12D984] disabled:opacity-40 transition-colors"
          data-testid="settings-save-btn"
        >
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
